/**
 * Customer accounts as seen by the desktop (spec section 5, 16).
 *
 * local-accounts change (2026-09-24): accounts are created locally on the desktop; the server is a
 * telemetry registry (mirror of what devices report plus admin edits), never a gate. `GET /v1/accounts`
 * is the registry browse and `POST /v1/accounts/reports` carries allowlisted account telemetry.
 */
import { z } from 'zod';
import { AccountIdSchema, AccountProviderSchema, OperationIdSchema, UserIdSchema } from '../ids.js';
import {
  CountSchema,
  CursorQuerySchema,
  EmptyRequestSchema,
  IsoDateTimeSchema,
  QueryBooleanSchema,
  RevisionSchema,
  defineRoute,
  pageOf,
} from './common.js';
import { AccountLifecycleSchema, AccountSchema, AssignmentSchema } from './entities.js';

/**
 * @deprecated local-accounts change: the assignment-filtered list is gone. `accounts.list` now takes
 * `AccountRegistryBrowseQuerySchema`. Kept so existing imports resolve; not used by any route.
 */
export const AccountListQuerySchema = CursorQuerySchema.extend({
  lifecycle: AccountLifecycleSchema.optional(),
  provider: AccountProviderSchema.optional(),
  search: z.string().max(120).optional(),
});
export type AccountListQuery = z.infer<typeof AccountListQuerySchema>;

/** @deprecated local-accounts change: see `AccountRegistryBrowseResponseSchema`. */
export const AccountPageSchema = pageOf(AccountSchema);
export type AccountPage = z.infer<typeof AccountPageSchema>;

export const AccountParamsSchema = z.object({ accountId: AccountIdSchema });

// ---------------------------------------------------------------------------------------------
// local-accounts change: registry browse (GET /v1/accounts)
// ---------------------------------------------------------------------------------------------

/** One registry row: what devices have reported about a customer account. Never a gate. */
export const RegistryAccountSchema = z.object({
  /** Stable customer id; reusing it from the desktop keeps one customer on one id across analysts. */
  id: AccountIdSchema,
  displayName: z.string().min(1).max(120),
  provider: AccountProviderSchema,
  /** Distinct users whose devices currently report this account as present locally. */
  operatorCount: CountSchema,
  /** Devices currently reporting this account as present. */
  deviceCount: CountSchema,
  /** Distinct provider connections registered for the account (any device). */
  connectionCount: CountSchema,
  lastReportedSyncAt: IsoDateTimeSchema.nullable(),
  archived: z.boolean(),
  firstReportedAt: IsoDateTimeSchema,
});
export type RegistryAccount = z.infer<typeof RegistryAccountSchema>;

export const AccountRegistryBrowseQuerySchema = CursorQuerySchema.extend({
  search: z.string().max(120).optional(),
  /** Absent = active rows only. Query-string safe (`true`/`false`/`1`/`0`). */
  includeArchived: QueryBooleanSchema.optional(),
});
export type AccountRegistryBrowseQuery = z.infer<typeof AccountRegistryBrowseQuerySchema>;

export const AccountRegistryBrowseResponseSchema = pageOf(RegistryAccountSchema);
export type AccountRegistryBrowseResponse = z.infer<typeof AccountRegistryBrowseResponseSchema>;

// ---------------------------------------------------------------------------------------------
// local-accounts change: account telemetry reports (POST /v1/accounts/reports)
// ---------------------------------------------------------------------------------------------

export const ACCOUNT_REPORT_ACTIONS = ['created', 'renamed', 'notes_updated', 'archived', 'unarchived', 'removed_local'] as const;
export const AccountReportActionSchema = z.enum(ACCOUNT_REPORT_ACTIONS);
export type AccountReportAction = z.infer<typeof AccountReportActionSchema>;

export const ACCOUNT_REPORTS_MAX = 100;

/**
 * Allowlisted metadata only: stable id, display name, provider, action, timestamps, operation id.
 * Notes are never reported (`notes_updated` carries the fact, not the text). Strict: extra keys fail.
 */
export const AccountReportSchema = z
  .object({
    id: AccountIdSchema,
    displayName: z.string().min(1).max(120),
    provider: AccountProviderSchema,
    action: AccountReportActionSchema,
    occurredAt: IsoDateTimeSchema,
    /** Desktop operation id; the server is idempotent on (deviceId, operationId). */
    operationId: OperationIdSchema,
  })
  .strict();
export type AccountReport = z.infer<typeof AccountReportSchema>;

export const AccountReportsRequestSchema = z
  .object({ reports: z.array(AccountReportSchema).min(1).max(ACCOUNT_REPORTS_MAX) })
  .strict();
export type AccountReportsRequest = z.infer<typeof AccountReportsRequestSchema>;

/**
 * Replays of an already-applied (deviceId, operationId) are returned in `accepted` (idempotent).
 * `rejected` reasons are short codes; the desktop drops rejected reports after auditing them.
 */
export const ACCOUNT_REPORT_REJECT_REASONS = ['schema_invalid', 'provider_mismatch', 'unknown_account'] as const;
export const AccountReportRejectReasonSchema = z.enum(ACCOUNT_REPORT_REJECT_REASONS);
export type AccountReportRejectReason = z.infer<typeof AccountReportRejectReasonSchema>;

export const AccountReportsResponseSchema = z.object({
  accepted: z.array(AccountIdSchema),
  rejected: z.array(z.object({ id: AccountIdSchema, reason: AccountReportRejectReasonSchema })),
});
export type AccountReportsResponse = z.infer<typeof AccountReportsResponseSchema>;

// ---------------------------------------------------------------------------------------------
// Register / rename / archive / assign (server-side registry metadata)
// ---------------------------------------------------------------------------------------------

export const AccountRegisterRequestSchema = z
  .object({
    /**
     * local-accounts change: optional client-generated stable id (create-with-id for compatibility).
     * Absent = the server issues the id.
     */
    id: AccountIdSchema.optional(),
    displayName: z.string().min(1).max(120),
    provider: AccountProviderSchema,
    notes: z.string().max(2000).optional(),
    /** Assign the creator immediately (default true). Informational only; assignments are not enforced. */
    assignSelf: z.boolean().default(true),
  })
  .strict();
export type AccountRegisterRequest = z.infer<typeof AccountRegisterRequestSchema>;

export const AccountRenameRequestSchema = z
  .object({ displayName: z.string().min(1).max(120), revision: RevisionSchema })
  .strict();
export const AccountArchiveRequestSchema = z.object({ revision: RevisionSchema }).strict();

export const AccountAssignRequestSchema = z
  .object({
    /** Full replacement set of assigned users. */
    userIds: z.array(UserIdSchema).max(500),
    revision: RevisionSchema,
  })
  .strict();
export type AccountAssignRequest = z.infer<typeof AccountAssignRequestSchema>;

export const AccountAssignResponseSchema = z.object({
  account: AccountSchema,
  assignments: z.array(AssignmentSchema),
});

export const AccountResponseSchema = z.object({ account: AccountSchema });

export const accountRoutes = {
  // local-accounts change: registry browse for any authenticated desktop user; no assignment filter.
  'accounts.list': defineRoute({
    id: 'accounts.list',
    method: 'GET',
    path: '/v1/accounts',
    auth: 'desktop',
    requestSchema: AccountRegistryBrowseQuerySchema,
    responseSchema: AccountRegistryBrowseResponseSchema,
    description: 'Registry browse: accounts reported by any device, with search, cursor and archived filter. Never a gate.',
  }),
  // local-accounts change: desktop telemetry for local account lifecycle (allowlisted metadata only).
  'accounts.reports': defineRoute({
    id: 'accounts.reports',
    method: 'POST',
    path: '/v1/accounts/reports',
    auth: 'desktop',
    requestSchema: AccountReportsRequestSchema,
    responseSchema: AccountReportsResponseSchema,
    description: 'Ingest local account reports: upsert registry rows by id, record per-device presence, write account audit events; idempotent by (deviceId, operationId).',
  }),
  'accounts.get': defineRoute({
    id: 'accounts.get',
    method: 'GET',
    path: '/v1/accounts/:accountId',
    auth: 'desktop',
    paramsSchema: AccountParamsSchema,
    requestSchema: z.object({}).loose(),
    responseSchema: AccountResponseSchema,
    description: 'One registry account.',
  }),
  /**
   * @deprecated local-accounts change: accounts are created locally (`accounts.create` IPC) and reported
   * via `accounts.reports`. Kept as create-with-id for compatibility; may be removed in a later release.
   */
  'accounts.register': defineRoute({
    id: 'accounts.register',
    method: 'POST',
    path: '/v1/accounts',
    auth: 'desktop',
    capability: 'account.register',
    requestSchema: AccountRegisterRequestSchema,
    responseSchema: AccountResponseSchema,
    description: 'Deprecated: pre-create a registry entry (optionally with a client id). Local creation reports via accounts.reports.',
  }),
  'accounts.rename': defineRoute({
    id: 'accounts.rename',
    method: 'PATCH',
    path: '/v1/accounts/:accountId',
    auth: 'desktop',
    capability: 'account.register',
    paramsSchema: AccountParamsSchema,
    requestSchema: AccountRenameRequestSchema,
    responseSchema: AccountResponseSchema,
    description: 'Rename a registry account (revision-checked). Local names win on the desktop.',
  }),
  'accounts.archive': defineRoute({
    id: 'accounts.archive',
    method: 'POST',
    path: '/v1/accounts/:accountId/archive',
    auth: 'desktop',
    capability: 'account.register',
    paramsSchema: AccountParamsSchema,
    requestSchema: AccountArchiveRequestSchema,
    responseSchema: AccountResponseSchema,
    description: 'Archive a registry account. Local data remains until deleted locally.',
  }),
  'accounts.assign': defineRoute({
    id: 'accounts.assign',
    method: 'PUT',
    path: '/v1/accounts/:accountId/assignments',
    auth: 'desktop',
    capability: 'account.assign',
    paramsSchema: AccountParamsSchema,
    requestSchema: AccountAssignRequestSchema,
    responseSchema: AccountAssignResponseSchema,
    description: 'Replace the expected-operator set (informational metadata; nothing enforces it).',
  }),
  'accounts.unarchive': defineRoute({
    id: 'accounts.unarchive',
    method: 'POST',
    path: '/v1/accounts/:accountId/unarchive',
    auth: 'desktop',
    capability: 'account.register',
    paramsSchema: AccountParamsSchema,
    requestSchema: EmptyRequestSchema,
    responseSchema: AccountResponseSchema,
    description: 'Restore an archived registry account.',
  }),
} as const;
